import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkRating, {
  RATING_SIZES,
  RATING_SCALES,
} from '@skyscanner-internal/backpack-web/bpk-component-rating';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useMBDContext } from '../../common/MBD/useMBDContext';
import DetailRating from '../DetailRating/DetailRating';
import Footer from '../Footer/Footer';
import PickUpTypes from '../PickUpType/PickUpType';
import ProviderLogo from '../ProviderLogo/ProviderLogo';

import type { ProviderInfo } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ProviderRating';

import STYLES from './RatingsCard.module.scss';

type RatingCardProps = {
  provider: ProviderInfo;
  strings: { [key: string]: string };
  index: number;
};

const RatingsCard = ({ index, provider, strings }: RatingCardProps) => {
  const { pickUpTypes, ratingInfo } = provider;

  const overallRating = ratingInfo?.overall || 0;
  const reviewIsValid = ratingInfo?.reviews && ratingInfo?.reviews > 1;
  let ratingTitle = '';
  if (overallRating >= 4.9) {
    ratingTitle = strings.excellent;
  } else if (overallRating >= 4) {
    ratingTitle = strings.good;
  } else if (overallRating > 1) {
    ratingTitle = strings.okay;
  } else {
    ratingTitle = strings.no_rating;
  }
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const mbdName = `${rawComponentName}_item`;

  return (
    <BpkCard
      className={STYLES.RatingsCard}
      href={provider.link}
      target={provider.isOpenInNewTab ? '_blank' : ''}
      rel={provider.isRelFollow ? '' : 'nofollow'}
      role="link"
      data-tracking-element-id={`${mbdName}_${index}`}
      data-tracking-common-params={buildMBDParam(mbdName, {
        id: provider.providerId,
        name: provider.providerName,
      })}
    >
      <div className={STYLES.RatingsCard__topSection}>
        <div className={STYLES.RatingsCard__logo}>
          <ProviderLogo provider={provider} />
        </div>
        {ratingInfo?.overall && ratingInfo?.overall > 1 ? (
          <div
            className={STYLES.RatingsCard__overallRating}
            aria-label={ratingInfo?.ariaLabel?.replace(
              '@@rating_text@@',
              ratingTitle,
            )}
            role="status"
          >
            <BpkRating
              ariaLabel={ratingTitle}
              title={ratingTitle}
              size={RATING_SIZES.large}
              subtitle={reviewIsValid ? ratingInfo?.reviewsNumber : ''}
              value={ratingInfo?.overall.toFixed(1)}
              ratingScale={RATING_SCALES.zeroToFive}
            />
          </div>
        ) : (
          // When rating is invalid, the rating number is not displayed
          <div
            className={STYLES.RatingsCard__invalidRating}
            aria-label={ratingInfo?.ariaLabel?.replace(
              '@@rating_text@@',
              ratingTitle,
            )}
            role="status"
          >
            <BpkText
              color={TEXT_COLORS.textPrimary}
              textStyle={TEXT_STYLES.heading5}
            >
              {ratingTitle}
            </BpkText>
            {reviewIsValid && (
              <BpkText color={TEXT_COLORS.textSecondary}>
                {ratingInfo?.reviewsNumber}
              </BpkText>
            )}
          </div>
        )}
      </div>
      {ratingInfo && (
        <div className={STYLES.RatingsCard__middleSection}>
          {pickUpTypes && <PickUpTypes pickupTypes={pickUpTypes} />}
          <DetailRating ratingInfo={ratingInfo} />
        </div>
      )}
      <div className={STYLES.RatingsCard__divided} />
      <Footer strings={strings} provider={provider} />
    </BpkCard>
  );
};

export default RatingsCard;
